import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAllMakes, getAllMotorcycleModels, createMotorcycleModel, updateMotorcycleModel, deleteMotorcycleModel } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, X, Trash2, AlertCircle, Search } from 'lucide-react';

export default function ModelsAdmin() {
  const location = useLocation();
  const [models, setModels] = useState([]);
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selectedMake, setSelectedMake] = useState('');
  const [modelName, setModelName] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadMakes();
  }, []);

  useEffect(() => {
    if (makes.length > 0) {
      loadModels(selectedMake);
    }
  }, [selectedMake, makes]);

  const loadMakes = async () => {
    try {
      const makesData = await getAllMakes();
      setMakes(makesData);
    } catch (err) {
      console.error('Failed to load makes:', err);
    }
  };

  const loadModels = async (make) => {
    setLoading(true);
    try {
      const modelsData = await getAllMotorcycleModels(make);
      setModels(modelsData);
    } catch (err) {
      console.error('Failed to load models:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedMake || !modelName.trim()) return;
    try {
      const newModel = await createMotorcycleModel({ make: selectedMake, name: modelName.trim() });
      setModels([...models, newModel]);
      setModelName('');
    } catch (err) {
      alert(err.error || 'Failed to add model');
    }
  };

  const handleUpdate = async (id) => {
    if (!selectedMake || !modelName.trim()) return;
    try {
      const updated = await updateMotorcycleModel(id, { make: selectedMake, name: modelName.trim() });
      setModels(models.map(m => m._id === id ? updated : m));
      setEditing(null);
      setModelName('');
    } catch (err) {
      alert(err.error || 'Failed to update model');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await deleteMotorcycleModel(deleteDialog._id);
      setModels(models.filter(m => m._id !== deleteDialog._id));
    } catch (err) {
      alert('Failed to delete model');
    } finally {
      setDeleteDialog(null);
    }
  };

  const startEdit = (model) => {
    setEditing(model._id);
    setSelectedMake(model.make);
    setModelName(model.name);
  };

  const filteredModels = searchQuery
    ? models.filter(m => 
        m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.make.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : models;

  const isSettingsRoute = location.pathname.startsWith('/admin/settings');

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">
        {isSettingsRoute ? 'Motorcycle Models' : 'Makes & Models'}
      </h1>

      {/* Filter by Make */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Filter by Make</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedMake} onValueChange={(val) => { setSelectedMake(val); loadModels(val); }}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All Makes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Makes</SelectItem>
              {makes.map(m => (
                <SelectItem key={m._id} value={m.name}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Add/Edit Form */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{editing ? 'Edit Model' : 'Add New Model'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedMake} onValueChange={setSelectedMake}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select Make" />
              </SelectTrigger>
              <SelectContent>
                {makes.map(m => (
                  <SelectItem key={m._id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Model name (e.g., CBR 600RR)"
              value={modelName}
              onChange={e => setModelName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (editing ? handleUpdate(editing) : handleAdd())}
              className="max-w-sm"
            />
            {editing ? (
              <Button onClick={() => handleUpdate(editing)}>Update</Button>
            ) : (
              <Button onClick={handleAdd} variant="default">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            )}
            {editing && (
              <Button variant="outline" onClick={() => { setEditing(null); setModelName(''); }}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search models..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30%]">Make</TableHead>
              <TableHead className="w-[40%]">Model Name</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-sm text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredModels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-sm text-slate-400">
                  {searchQuery ? 'No models match your search' : 'No models yet. Add one above.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredModels.map(m => (
                <TableRow key={m._id}>
                  <td>
                    <Badge variant="secondary" className="font-medium">{m.make}</Badge>
                  </td>
                  <td className="font-medium text-slate-900">{m.name}</td>
                  <td className="text-sm text-slate-500">
                    {new Date(m.createdAt || m.created_at || 0).toLocaleDateString()}
                  </td>
                  <td className="text-right">
                    {!editing || editing !== m._id ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-700"
                          onClick={() => startEdit(m)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteDialog(m)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-indigo-600 font-medium">Editing...</span>
                    )}
                  </td>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Delete Confirmation Dialog */}
      {deleteDialog && (
        <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Delete Model
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{deleteDialog.name}" from "{deleteDialog.make}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}